import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import dayjs from 'utils/dayjs';
import {
  PatientAuthorizationAllowanceBasis,
} from 'dtos/patientAuthorizationAllowanceBasis';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';
import type {
  SelectorRow,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/types';

type GroupedAuthorizationRecord = ClaimAuthorizationOption['authorizations'][number];

type AllowanceSummary = GroupedAuthorizationRecord['allowances'][number];

export const selectorRowHighlightClasses =
  'aria-selected:!bg-blue-50 aria-selected:border-l-2 aria-selected:!border-l-blue-500';

export function normalizeAuthorizationNumber(value?: string | null): string {
  return value?.trim().toLowerCase() ?? '';
}

export function findExactOption(
  options: ClaimAuthorizationOption[],
  authorizationNumber?: string | null,
): ClaimAuthorizationOption | undefined {
  const normalized = normalizeAuthorizationNumber(authorizationNumber);
  return notEmptyString(normalized)
    ? options.find((option) => (
      normalizeAuthorizationNumber(option.authorizationNumber) === normalized
    ))
    : undefined;
}

function getUpdatedOnValue(updatedOn?: string | null): number {
  if (!notEmptyString(updatedOn)) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = dayjs(updatedOn).valueOf();
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
}

export function getLatestGroupedAuthorizationRecord(
  authorizations: ClaimAuthorizationOption['authorizations'],
): GroupedAuthorizationRecord | undefined {
  return authorizations.filter((authorization) => authorization.allowances.length > 0).sort((left, right) => {
    const updatedOnDiff = getUpdatedOnValue(right.updatedOn) - getUpdatedOnValue(left.updatedOn);
    return updatedOnDiff !== 0
      ? updatedOnDiff
      : right.patientAuthorizationId - left.patientAuthorizationId;
  })[0];
}

export function formatAllowanceSummary(allowance: AllowanceSummary): string {
  const used = allowance.usedQuantity + allowance.externalUsedQuantity;
  const basisLabel = allowance.allowanceBasis === PatientAuthorizationAllowanceBasis.Units
    ? 'units'
    : 'visits';
  const quantityLabel = `${used} of ${allowance.approvedQuantity} ${basisLabel} used`;
  const distinctCodes = Array.from(new Set(allowance.codes.map(({ code }) => code)))
    .filter((code) => notEmptyString(code));

  return distinctCodes.length > 0
    ? `${distinctCodes.join(' · ')} · ${quantityLabel}`
    : quantityLabel;
}

export function buildDisplayRows(
  draft: string,
  options: ClaimAuthorizationOption[],
): SelectorRow[] {
  const trimmed = draft.trim();
  const normalizedDraft = trimmed.toLowerCase();
  const exactMatch = findExactOption(options, trimmed);
  const authorizationRows = options.map((option) => ({
    kind: 'authorization' as const,
    option,
  }));

  if (!notEmptyString(trimmed)) {
    return authorizationRows;
  }

  if (!isNil(exactMatch)) {
    return [{ kind: 'authorization' as const, option: exactMatch }];
  }

  const matching = options.filter((option) => (
    option.authorizationNumber.toLowerCase().includes(normalizedDraft)
  ));

  return [
    { kind: 'use' as const, typedNumber: trimmed },
    ...matching.map((option) => ({ kind: 'authorization' as const, option })),
    { kind: 'add' as const, typedNumber: trimmed },
  ];
}

export function getSelectorRowKey(row: SelectorRow): string {
  return row.kind === 'authorization'
    ? `authorization:${row.option.authorizationNumber}`
    : `${row.kind}:${row.typedNumber}`;
}
